import { Check, FileUp, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  useAddJDQuestionFile,
  useAddJDQuestions,
  type JDQuestions,
  type Scope,
} from "@/api/jd-question-queries";
import type { JDDetail } from "@/api/jd-queries";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Input, Textarea } from "@/components/ui/input";
import { Empty, ErrorNote } from "@/components/ui/panel";
import { Segmented } from "@/components/ui/segmented";
import { cn } from "@/lib/utils";

/**
 * Adding questions to a JD — DESIGN §20.6. Paste a list, write one, or upload a file, at any time and in
 * any status but archived or discarded; each goes through the pipeline and the Review queue and serves
 * the JD once approved. A drawer opened from the Questions step, so the list it adds to stays in view.
 */

type Mode = "list" | "one" | "file";

// core/jd_questions.py:_MARKER — numbering and bullets a pasted list arrives with. The preview only;
// the API splits again, and its answer is the one that counts.
const MARKER = /^\s*(?:[-*•·–—]+|\(?\d{1,3}[.)\]:]|q(?:uestion)?\s*\d{1,3}\s*[.):\-–]?)\s+/i;

function splitList(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const q = line.replace(MARKER, "").split(/\s+/).join(" ").trim();
    const key = q.toLowerCase();
    if (q && !seen.has(key)) {
      seen.add(key);
      out.push(q);
    }
  }
  return out;
}

function Form({ jd, data }: { jd: JDDetail; data: JDQuestions }) {
  const [mode, setMode] = useState<Mode>("list");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [scope, setScope] = useState<Scope>(data.default_scope);
  const add = useAddJDQuestions(jd.jd.id);
  const upload = useAddJDQuestionFile(jd.jd.id);
  const found = mode === "list" ? splitList(text) : mode === "one" && text.trim() ? [text.trim()] : [];
  const busy = add.isPending || upload.isPending;
  const result = add.data ?? upload.data;

  function submit() {
    const options = { onSuccess: () => (setText(""), setFile(null)) };
    if (mode === "file") {
      if (file) upload.mutate({ file, stack: null, scope }, options);
    } else {
      add.mutate({ text, one: mode === "one", stack: null, scope }, options);
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <Segmented<Mode>
        label="How to add questions"
        value={mode}
        onChange={setMode}
        options={[
          { value: "list", label: "Paste a list" },
          { value: "one", label: "Write one" },
          { value: "file", label: "Upload a file" },
        ]}
      />

      {mode === "file" ? (
        <div>
          <label htmlFor="jdq-file" className="mb-1 block text-[12.5px] font-medium">
            A document of questions <span className="text-bank-muted font-normal">— pdf, docx or txt</span>
          </label>
          <Input
            id="jdq-file"
            type="file"
            accept=".pdf,.docx,.txt"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <p className="text-bank-muted mt-1 text-xs">
            Read by the extraction model like any upload, so it spends one AI request per few pages.
          </p>
        </div>
      ) : (
        <div>
          <label htmlFor="jdq-text" className="mb-1 block text-[12.5px] font-medium">
            {mode === "list" ? (
              <>
                One question per line{" "}
                <span className="text-bank-muted font-normal">— numbering and bullets are fine</span>
              </>
            ) : (
              "The question"
            )}
          </label>
          <Textarea
            id="jdq-text"
            rows={mode === "list" ? 6 : 3}
            className={mode === "list" ? "font-mono text-xs" : undefined}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      )}

      {mode === "list" && found.length > 0 && (
        <div className="bg-bank-surface-2 rounded-md px-3 py-2.5">
          <p className="text-bank-muted mb-1.5 font-mono text-[10.5px] tracking-[0.07em] uppercase">
            {found.length} question{found.length === 1 ? "" : "s"} found
          </p>
          <ol className="list-decimal space-y-0.5 pl-5 text-[12.5px]">
            {found.slice(0, 8).map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ol>
          {found.length > 8 && <p className="text-bank-muted mt-1 text-xs">and {found.length - 8} more</p>}
          <p className="text-bank-muted mt-1.5 text-[11.5px]">A clean list skips the AI extraction step.</p>
        </div>
      )}

      <div>
        <span className="mb-1 block text-[12.5px] font-medium">Suggest to the reviewer</span>
        <div role="radiogroup" aria-label="Suggest to the reviewer" className="flex flex-wrap gap-2">
          {(
            [
              ["jd_only", "This JD only", "Asked only in this JD's interviews"],
              ["bank", "Whole bank", "Any interview may ask it"],
            ] as const
          ).map(([value, title, text]) => (
            <label
              key={value}
              className={cn(
                "flex flex-[1_1_160px] cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2",
                scope === value ? "border-bank-accent bg-bank-accent-wash" : "border-bank-border-strong",
              )}
            >
              <input
                type="radio"
                name="jdq-scope"
                className="accent-bank-accent mt-1"
                checked={scope === value}
                onChange={() => setScope(value)}
              />
              <span className="text-[13px]">
                <strong>{title}</strong>
                {data.default_scope === value && <span className="text-bank-muted"> (default)</span>}
                <span className="text-bank-muted block text-xs">{text}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <p className="bg-bank-accent-wash text-bank-accent-ink rounded-md px-3 py-2 text-[12.5px]">
        {data.stacks.length > 0
          ? `Each question is tagged against this JD's stacks (${data.stacks.join(", ")}). `
          : "This JD maps no stack yet, so the model tags each question on its own. "}
        It goes through the pipeline and review, and serves this JD once approved. One already in the bank is
        linked instead of added again. Interviews already started keep their questions.
      </p>

      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          variant="primary"
          disabled={busy || (mode === "file" ? !file : found.length === 0)}
          onClick={submit}
        >
          {mode === "file" ? (
            <FileUp className="size-4" aria-hidden="true" />
          ) : (
            <Plus className="size-4" aria-hidden="true" />
          )}
          {mode === "file" ? "Upload" : `Add ${found.length} question${found.length === 1 ? "" : "s"}`}
        </Button>
        <span className="text-bank-muted text-xs">
          You can add questions at any time, even once it's live.
        </span>
      </div>
      <ErrorNote error={add.error ?? upload.error} />
      {result && (
        <p className="bg-bank-good-wash text-bank-good-ink flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-[12.5px]">
          <Check className="size-4" aria-hidden="true" />
          {mode !== "file"
            ? `${result.added} added${result.linked ? `, ${result.linked} already in the bank and linked` : ""}.`
            : "Queued for extraction."}
          {result.pipeline_job && (
            <Link to="/ingest" className="underline">
              Pipeline job #{result.pipeline_job.job_id}
            </Link>
          )}
        </p>
      )}
    </div>
  );
}

export function AddQuestionsDrawer({
  jd,
  data,
  open,
  onClose,
}: {
  jd: JDDetail;
  data: JDQuestions;
  open: boolean;
  onClose: () => void;
}) {
  const closed = jd.jd.status === "archived" || jd.jd.status === "discarded";
  return (
    <Drawer open={open} onClose={onClose} title="Add questions to this JD">
      {closed ? (
        <Empty>It is {jd.jd.status}. Put it back in review to add questions.</Empty>
      ) : (
        <Form jd={jd} data={data} />
      )}
    </Drawer>
  );
}
